import { LEVEL_XP, levelAtXp } from '@/utils/levels';

/**
 * Onde o jogador está na escala da Smartico — o número do nível e a régua de XP.
 *
 * Existe separado de `useLevelsProgress` porque não é só a tela de níveis que
 * precisa do nível: a celebração de subida de elo (`LevelUpWatcher`, montado no
 * `app.vue`) também precisa, e ela não pode chamar o composable da tela, que
 * carrega estado de navegação do carrossel. Enquanto essa leitura estava
 * duplicada nos dois, um jogador no NÍVEL 4 (fim do Bronze) era lido como 5 pela
 * celebração: ela disparava a festa da Prata um nível antes e gravava a marca,
 * de modo que a subida real nunca mais celebrava.
 *
 * Só leitura derivada: quem carrega os dados é a store.
 */
export function useSmarticoLevel() {
  const { userInfosProfile, currentUserLevelData, userLevelNumber } =
    storeToRefs(useSmarticoUserInfos());

  const { allLevels } = storeToRefs(useSmarticoLevels());

  /** XP acumulado do jogador. Vem direto da Smartico. */
  const xp = computed(() => userInfosProfile.value?.ach_points_ever ?? 0);

  /**
   * A régua da Smartico chegou e a store conseguiu situar o jogador nela.
   *
   * NÃO exige `userLevelNumber > 0`: era essa guarda que descartava o jogador de
   * nível 0 — o degrau em que toda conta nova nasce — e derrubava a tela no
   * fallback local, onde ele aparecia como "Nível 1".
   */
  const hasSmarticoLevels = computed(() => !!currentUserLevelData.value);

  /**
   * Régua efetiva de XP, indexada pelo próprio nível (`table[n]` = nível n).
   * `allLevels` já vem ordenado por `required_points` na store.
   *
   * A régua da Smartico já traz o nível 0 como primeiro degrau (0 XP), então ela
   * entra direto. Se um dia vier sem ele, prefixamos o 0 para a indexação
   * continuar valendo — assim `xpForLevelIn` não precisa saber de onde veio.
   */
  const levelXpTable = computed<number[]>(() => {
    if (allLevels.value?.length) {
      const table = allLevels.value.map((entry) => entry.required_points);

      return table[0] === 0 ? table : [0, ...table];
    }

    return [...LEVEL_XP];
  });

  /** Último degrau: o índice É o nível, então o teto é `length - 1`. */
  const maxLevel = computed(() => levelXpTable.value.length - 1);

  /**
   * A régua da Smartico começa no nível 0 (visto na conta real:
   * `ach_level_current: "NÍVEL 0"`, primeiro item de `getLevels()`).
   */
  const ladderStartsAtZero = computed(
    () => allLevels.value?.[0]?.required_points === 0,
  );

  /** Posição do jogador dentro da régua, ou -1 se ela não o contém. */
  const ladderIndex = computed(() => {
    const id = currentUserLevelData.value?.id;

    if (!id || !allLevels.value?.length) return -1;

    return allLevels.value.findIndex((entry) => entry?.id === id);
  });

  /**
   * Nível do jogador.
   *
   * Lido da POSIÇÃO na régua, não do `userLevelNumber` da store: a store publica
   * `indexLevel + 1` (contrato 1-based que não pode mudar — alimenta o
   * `LevelsSlide` da Cactus e o `ordinal_position` do payload do backoffice), e
   * como o primeiro degrau da régua é o nível 0 esse `+1` adiantava tudo um
   * degrau: a conta nova aparecia como "Nível 1" e cada linha da trilha
   * anunciava o XP do nível anterior.
   *
   * Ancorar no índice também cobre o segundo caminho da store
   * (`loadCurrentLevelData`), que preenche `userLevelNumber` com o
   * `ordinal_position` da Smartico e NÃO passa pelo `+1` — subtrair 1 às cegas
   * teria atrasado um degrau justamente esse caso.
   */
  const level = computed(() => {
    if (!hasSmarticoLevels.value) return levelAtXp(xp.value);

    if (ladderIndex.value >= 0) {
      return ladderStartsAtZero.value
        ? ladderIndex.value
        : ladderIndex.value + 1;
    }

    return Math.max(0, userLevelNumber.value);
  });

  return {
    xp,
    level,
    hasSmarticoLevels,
    levelXpTable,
    maxLevel,
  };
}
